import type { ChangeEvent } from "react";
import { useState } from "react";
import { useMutation } from "react-query";
import { t } from "@/i18n-js/instance";
import { useOtpChallenge } from "@/react/hooks/otpChallenge/useOtpChallenge";
import { useCurrentCommunityContext } from "@circle-react/apps/AuthenticationApp";
import { authenticationAppRoutes } from "@circle-react/components/OnboardingPage/helpers/routes";
import { reactQueryPost } from "@circle-react/helpers/backendRequestHelpers";
import { useSetCouponCodeFromParamsToCookie } from "@circle-react/hooks/useSetCouponCodeFromParamsToCookie";
import { Button } from "@circle-react-uikit/Button";
import { Form } from "@circle-react-uikit/Form";
import type { OnSubmitFunction } from "@circle-react-uikit/Form/Form";
import { useToast } from "@circle-react-uikit/ToastV2";

interface SignInFormState {
  chat_bot_session_id: string | null;
  source: string | null;
  user: {
    email: string;
    password: string;
    community_id?: number;
  };
}

interface SignInResponse {
  message?: string;
  redirect_url?: string;
}

interface SignInError {
  message: string;
}

export const SignInForm = () => {
  const { search } = location;
  const params = new URLSearchParams(search);
  const couponCode = params.get("coupon_code");
  const email = params.get("email");

  const urlParams = couponCode ? { coupon_code: couponCode } : {};

  useSetCouponCodeFromParamsToCookie();
  const { currentCommunity } = useCurrentCommunityContext();
  const toast = useToast();

  const localeScope = "authentication_app.signin.form";
  const buttonText = couponCode
    ? t(`${localeScope}.promotion_submit`)
    : t(`${localeScope}.submit`);
  const defaultFormState: SignInFormState = {
    user: {
      email: email || "",
      password: "",
      community_id: currentCommunity?.id,
    },
    source: params.get("source"),
    chat_bot_session_id: params.get("chat_bot_session_id"),
  };

  const isEmailAutoFilled = Boolean(email);
  const [inputEmail, setInputEmail] = useState<string>("");

  const { mutateAsync: signInWithOtp } = useOtpChallenge({
    mutationFn: (data: SignInFormState) =>
      reactQueryPost<SignInResponse>(authenticationAppRoutes.signIn(), data),
    email: inputEmail,
    otpType: "authentication",
  });

  const { mutateAsync: handleSubmit, isLoading } = useMutation<
    SignInResponse,
    SignInError,
    SignInFormState
  >(signInWithOtp, {
    onSuccess: data => {
      if (data?.redirect_url) {
        window.location.href = data.redirect_url;
      }
    },
    onError: (error: SignInError) => {
      toast.error(error.message);
    },
  });

  const onSubmit: OnSubmitFunction<SignInFormState> = async values => {
    await handleSubmit(values);
  };

  return (
    <Form<SignInFormState>
      action={authenticationAppRoutes.signIn({ params: urlParams })}
      method="POST"
      defaultValues={defaultFormState}
      onSubmit={onSubmit}
    >
      <Form.Item
        isFirstChild
        id="user_email"
        name="user[email]"
        label={t(`${localeScope}.email`)}
        hideDescription
        hideBorder
        rules={{
          required: t(`${localeScope}.errors.email_required`),
        }}
      >
        <Form.Input
          autoFocus={!isEmailAutoFilled}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setInputEmail(e.target.value)
          }
        />
      </Form.Item>

      <Form.Item
        id="user_password"
        name="user[password]"
        label={t(`${localeScope}.password`)}
        hideDescription
        hideBorder
        rules={{
          required: t(`${localeScope}.errors.password_required`),
        }}
      >
        <Form.Input type="password" autoFocus={isEmailAutoFilled} />
      </Form.Item>

      <Form.Item
        hiddenField
        name="user[community_id]"
        hideDescription
        hideBorder
        hideLabel
      >
        <Form.Input type="hidden" />
      </Form.Item>

      <div className="mt-6">
        <Button
          type="submit"
          name="commit"
          variant={currentCommunity ? "primary" : "marketing-gradient-blue"}
          disabled={isLoading}
          extraLarge
          full
        >
          {buttonText}
        </Button>
      </div>
    </Form>
  );
};
